import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import {
  UserPlus,
  Mail,
  ShieldCheck,
  Clock,
  MoreHorizontal,
  LogOut,
  Pencil,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { Database } from '@/types/supabase-types';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

type TeamMember = Database['public']['Tables']['publisher_users']['Row'];
type Invite = Database['public']['Tables']['publisher_invites']['Row'];

type MemberPermissions = {
  can_upload: boolean;
  can_publish: boolean;
  can_manage_team: boolean;
  can_view_analytics: boolean;
  can_manage_billing: boolean;
};

const PERMISSION_LABELS: { key: keyof MemberPermissions; label: string }[] = [
  { key: 'can_upload', label: 'Upload content' },
  { key: 'can_publish', label: 'Publish titles' },
  { key: 'can_view_analytics', label: 'View analytics' },
  { key: 'can_manage_team', label: 'Manage team' },
  { key: 'can_manage_billing', label: 'Manage billing' },
];

function memberInitials(name: string | null, email: string | null): string {
  const source = name || email || '?';
  return source
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0]!.toUpperCase())
    .join('');
}

function permissionBadges(member: TeamMember) {
  const active = PERMISSION_LABELS.filter(({ key }) => member[key] === true);
  if (active.length === 0) return null;
  return (
    <div className='mt-1 flex flex-wrap gap-1'>
      {active.map(({ key, label }) => (
        <Badge key={key} variant='outline' className='text-[10px] font-normal'>
          {label}
        </Badge>
      ))}
    </div>
  );
}

function InviteDialog({
  publisherId,
  onInvited,
}: {
  publisherId: string;
  onInvited: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'member' | 'admin'>('member');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { error } = await supabase.rpc('invite_to_studio', {
        target_publisher_id: publisherId,
        member_email: email.trim(),
        member_role: role,
      });
      if (error) throw error;
      toast.success(`Invite sent to ${email.trim()} — they'll see it in the Guild`);
      setOpen(false);
      setEmail('');
      onInvited();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to send invite');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <UserPlus className='h-4 w-4' /> Invite member
        </Button>
      </DialogTrigger>
      <DialogContent className='sm:max-w-md'>
        <DialogHeader>
          <DialogTitle>Invite a team member</DialogTitle>
          <DialogDescription>
            They must already be a Kinora Guild member. The invite appears in their
            guild account and they join the studio when they accept.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className='flex flex-col gap-4'>
          <div className='flex flex-col gap-2'>
            <Label htmlFor='invite-email'>Email</Label>
            <Input
              id='invite-email'
              type='email'
              required
              placeholder='creator@example.com'
              value={email}
              onChange={e => setEmail(e.target.value)}
            />
          </div>
          <div className='flex flex-col gap-2'>
            <Label>Role</Label>
            <Select value={role} onValueChange={v => setRole(v as 'member' | 'admin')}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='member'>Member — upload &amp; analytics</SelectItem>
                <SelectItem value='admin'>Admin — publish &amp; manage team</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button type='button' variant='outline' onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type='submit' disabled={saving}>
              {saving ? 'Sending…' : 'Send invite'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EditMemberDialog({
  member,
  open,
  onOpenChange,
  onSaved,
}: {
  member: TeamMember;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
}) {
  const [role, setRole] = useState<'member' | 'admin'>(
    member.role === 'admin' ? 'admin' : 'member'
  );
  const [permissions, setPermissions] = useState<MemberPermissions>({
    can_upload: member.can_upload ?? false,
    can_publish: member.can_publish ?? false,
    can_manage_team: member.can_manage_team ?? false,
    can_view_analytics: member.can_view_analytics ?? false,
    can_manage_billing: member.can_manage_billing ?? false,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setRole(member.role === 'admin' ? 'admin' : 'member');
    setPermissions({
      can_upload: member.can_upload ?? false,
      can_publish: member.can_publish ?? false,
      can_manage_team: member.can_manage_team ?? false,
      can_view_analytics: member.can_view_analytics ?? false,
      can_manage_billing: member.can_manage_billing ?? false,
    });
  }, [member]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { error } = await supabase.rpc('update_studio_member', {
        member_id: member.id,
        member_role: role,
        p_can_upload: permissions.can_upload,
        p_can_publish: permissions.can_publish,
        p_can_manage_team: permissions.can_manage_team,
        p_can_view_analytics: permissions.can_view_analytics,
        p_can_manage_billing: permissions.can_manage_billing,
      });
      if (error) throw error;
      toast.success('Team member updated');
      onOpenChange(false);
      onSaved();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to update member');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-md'>
        <DialogHeader>
          <DialogTitle>Edit team member</DialogTitle>
          <DialogDescription>
            {member.full_name || member.email}
          </DialogDescription>
        </DialogHeader>
        <div className='flex flex-col gap-4'>
          <div className='flex flex-col gap-2'>
            <Label>Role</Label>
            <Select value={role} onValueChange={v => setRole(v as 'member' | 'admin')}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='member'>Member</SelectItem>
                <SelectItem value='admin'>Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className='flex flex-col gap-3'>
            <Label>Permissions</Label>
            {PERMISSION_LABELS.map(({ key, label }) => (
              <label key={key} className='flex items-center gap-2 text-sm'>
                <Checkbox
                  checked={permissions[key]}
                  onCheckedChange={checked =>
                    setPermissions(prev => ({ ...prev, [key]: checked === true }))
                  }
                />
                {label}
              </label>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button variant='outline' onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Team() {
  const { publisher, user, membership, canManageTeam, refreshPublisher } = useAuth();
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [invites, setInvites] = useState<Invite[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingMember, setEditingMember] = useState<TeamMember | null>(null);

  const manager = canManageTeam();
  const isTeamMemberOnly = !!membership && membership.role !== 'owner';

  const load = useCallback(async () => {
    if (!publisher) return;
    try {
      const { data: memberData, error: memberError } = await supabase
        .from('publisher_users')
        .select('*')
        .eq('publisher_id', publisher.id)
        .eq('is_active', true)
        .order('created_at');
      if (memberError) throw memberError;
      setMembers(memberData ?? []);

      if (manager) {
        const { data: inviteData } = await supabase
          .from('publisher_invites')
          .select('*')
          .eq('publisher_id', publisher.id)
          .eq('status', 'pending')
          .order('created_at', { ascending: false });
        setInvites(inviteData ?? []);
      } else {
        setInvites([]);
      }
    } catch (error) {
      console.warn('Team load failed:', error);
      toast.error('Failed to load team');
    } finally {
      setLoading(false);
    }
  }, [publisher, manager]);

  useEffect(() => {
    load();
  }, [load]);

  const revokeInvite = async (invite: Invite) => {
    try {
      const { error } = await supabase
        .from('publisher_invites')
        .update({ status: 'revoked' })
        .eq('id', invite.id);
      if (error) throw error;
      toast.success('Invite revoked');
      load();
    } catch {
      toast.error('Failed to revoke invite');
    }
  };

  const removeMember = async (member: TeamMember) => {
    try {
      const { error } = await supabase.rpc('remove_studio_member', {
        member_id: member.id,
      });
      if (error) throw error;
      toast.success(`${member.full_name || member.email} removed from the team`);
      load();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to remove member');
    }
  };

  const leaveStudio = async () => {
    if (!publisher) return;
    try {
      const { error } = await supabase.rpc('leave_studio', {
        target_publisher_id: publisher.id,
      });
      if (error) throw error;
      toast.success('You left the studio');
      await refreshPublisher();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to leave studio');
    }
  };

  if (!publisher) return null;

  return (
    <div className='flex flex-col gap-6'>
      <Card>
        <CardHeader>
          <div className='flex flex-wrap items-center justify-between gap-3'>
            <div className='flex flex-col gap-1.5'>
              <CardTitle>Team</CardTitle>
              <CardDescription>
                People with access to {publisher.company_name}. Invite Kinora Guild
                members to collaborate.
              </CardDescription>
            </div>
            {manager && <InviteDialog publisherId={publisher.id} onInvited={load} />}
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className='flex flex-col gap-3'>
              {[...Array(2)].map((_, i) => (
                <Skeleton key={i} className='h-14' />
              ))}
            </div>
          ) : (
            <div className='flex flex-col divide-y divide-border'>
              {members.map(member => {
                const isOwner = member.role === 'owner';
                const isSelf = member.auth_user_id === user?.id;
                const canEdit = manager && !isOwner;

                return (
                  <div key={member.id} className='flex items-start gap-3 py-3'>
                    <Avatar className='h-9 w-9 mt-0.5'>
                      <AvatarFallback>
                        {memberInitials(member.full_name, member.email)}
                      </AvatarFallback>
                    </Avatar>
                    <div className='min-w-0 flex-1'>
                      <p className='truncate text-sm font-medium'>
                        {member.full_name || member.email}
                        {isSelf && (
                          <span className='ml-2 text-xs text-muted-foreground'>(you)</span>
                        )}
                      </p>
                      <p className='truncate text-xs text-muted-foreground'>{member.email}</p>
                      {permissionBadges(member)}
                    </div>
                    <Badge variant={isOwner ? 'default' : 'secondary'}>
                      {isOwner && <ShieldCheck className='h-3 w-3' />}
                      {member.role}
                    </Badge>
                    {canEdit && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant='ghost' size='icon' className='h-8 w-8 shrink-0'>
                            <MoreHorizontal className='h-4 w-4' />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align='end'>
                          <DropdownMenuItem onClick={() => setEditingMember(member)}>
                            <Pencil className='h-4 w-4' />
                            Edit role &amp; permissions
                          </DropdownMenuItem>
                          {!isSelf && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                className='text-destructive focus:text-destructive'
                                onClick={() => removeMember(member)}
                              >
                                Remove from team
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                );
              })}
              {members.length === 0 && (
                <p className='py-6 text-center text-sm text-muted-foreground'>
                  No team members yet.
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {manager && invites.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className='flex items-center gap-2'>
              <Clock className='h-4 w-4 text-muted-foreground' /> Pending invites
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className='flex flex-col divide-y divide-border'>
              {invites.map(invite => (
                <div key={invite.id} className='flex items-center gap-3 py-3'>
                  <Mail className='h-4 w-4 text-muted-foreground' />
                  <div className='min-w-0 flex-1'>
                    <p className='truncate text-sm'>{invite.email ?? 'Guild member'}</p>
                    <p className='text-xs text-muted-foreground'>
                      Invited as {invite.role}
                    </p>
                  </div>
                  <Button size='sm' variant='outline' onClick={() => revokeInvite(invite)}>
                    Revoke
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {isTeamMemberOnly && (
        <Card>
          <CardHeader>
            <CardTitle className='text-base'>Leave studio</CardTitle>
            <CardDescription>
              Remove your access to {publisher.company_name}. You can rejoin if invited again.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant='outline' onClick={leaveStudio}>
              <LogOut className='h-4 w-4' />
              Leave studio
            </Button>
          </CardContent>
        </Card>
      )}

      {editingMember && (
        <EditMemberDialog
          member={editingMember}
          open={!!editingMember}
          onOpenChange={open => {
            if (!open) setEditingMember(null);
          }}
          onSaved={load}
        />
      )}
    </div>
  );
}
